import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FaSearch, FaTimes } from "react-icons/fa";
import language from "../../localization";
import { AuthContext } from "../../authContext";
import { searchUniversity } from "../../services/search";
import SearchFilters from "./SearchFilters";
import SearchResultItem from "./SearchResultItem";
import styles from "./style.module.css";

const MIN_QUERY_LENGTH = 2;
const DEBOUNCE_MS = 320;
const RECENT_KEY = "saba_recent_searches";

const readRecentSearches = () => {
  try {
    return JSON.parse(localStorage.getItem(RECENT_KEY) || "[]").slice(0, 5);
  } catch {
    return [];
  }
};

const saveRecentSearch = (query) => {
  const value = query.trim();
  if (!value) return;

  const recent = readRecentSearches().filter((item) => item.toLowerCase() !== value.toLowerCase());
  localStorage.setItem(RECENT_KEY, JSON.stringify([value, ...recent].slice(0, 5)));
};

function SearchDialog({ open, onClose }) {
  const { languageCode } = useContext(AuthContext);
  const activeLanguage = language.normalizeLanguage(languageCode || language.getLanguage());
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const [query, setQuery] = useState("");
  const [activeType, setActiveType] = useState("all");
  const [results, setResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [recentSearches, setRecentSearches] = useState([]);
  const isRTL = language.isRTL(activeLanguage);
  const trimmedQuery = query.trim();
  const openResult = useCallback(
    (result) => {
      saveRecentSearch(trimmedQuery || result.title);
      setRecentSearches(readRecentSearches());
      onClose();
      navigate(result.url);
    },
    [navigate, onClose, trimmedQuery]
  );

  const openFullSearch = useCallback(
    (value = trimmedQuery) => {
      const finalQuery = value.trim();
      if (!finalQuery) return;

      saveRecentSearch(finalQuery);
      setRecentSearches(readRecentSearches());
      onClose();
      const params = new URLSearchParams();
      params.set("q", finalQuery);
      if (activeType !== "all") params.set("type", activeType);
      navigate(`/search?${params.toString()}`);
    },
    [activeType, navigate, onClose, trimmedQuery]
  );

  useEffect(() => {
    if (!open) return;
    setRecentSearches(readRecentSearches());
    setSelectedIndex(-1);
    window.setTimeout(() => inputRef.current?.focus(), 40);
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;

    const handler = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        onClose();
      }
      if (event.key === "ArrowDown") {
        event.preventDefault();
        setSelectedIndex((current) => Math.min(results.length - 1, current + 1));
      }
      if (event.key === "ArrowUp") {
        event.preventDefault();
        setSelectedIndex((current) => Math.max(-1, current - 1));
      }
      if (event.key === "Enter") {
        event.preventDefault();
        if (selectedIndex >= 0 && results[selectedIndex]) {
          openResult(results[selectedIndex]);
          return;
        }
        openFullSearch(trimmedQuery);
      }
    };

    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, results, selectedIndex, trimmedQuery, onClose, openResult, openFullSearch]);

  useEffect(() => {
    if (!open) return undefined;

    if (trimmedQuery.length < MIN_QUERY_LENGTH) {
      setResults([]);
      setIsLoading(false);
      setError("");
      return undefined;
    }

    const controller = new AbortController();
    const timeout = window.setTimeout(async () => {
      try {
        setIsLoading(true);
        setError("");
        const data = await searchUniversity({
          query: trimmedQuery,
          type: activeType,
          lang: activeLanguage,
          page: 1,
          limit: 8,
          signal: controller.signal,
        });
        setResults(data.results || []);
        setSelectedIndex(-1);
      } catch (err) {
        if (err.name !== "AbortError") {
          setError(language.search_unavailable);
          setResults([]);
        }
      } finally {
        if (!controller.signal.aborted) setIsLoading(false);
      }
    }, DEBOUNCE_MS);

    return () => {
      window.clearTimeout(timeout);
      controller.abort();
    };
  }, [open, trimmedQuery, activeType, activeLanguage]);


  const statusContent = (() => {
    if (!trimmedQuery) return <p className={styles.hint}>{language.search_intro}</p>;
    if (trimmedQuery.length < MIN_QUERY_LENGTH) return <p className={styles.hint}>{language.search_minimum_hint}</p>;
    if (isLoading) return <p className={styles.hint}>{language.searching}</p>;
    if (error) return <p className={styles.error}>{error}</p>;
    if (!results.length) {
      return (
        <div className={styles.emptyState}>
          <p>{language.search_no_results} "{trimmedQuery}"</p>
          <span>{language.search_try_broader}</span>
        </div>
      );
    }
    return null;
  })();

  if (!open) return null;

  return (
    <div className={styles.overlay} role="presentation" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <section
        className={styles.dialog}
        role="dialog"
        aria-modal="true"
        aria-labelledby="saba-search-title"
        dir={isRTL ? "rtl" : "ltr"}
      >
        <div className={styles.dialogHeader}>
          <div>
            <p className={styles.eyebrow}>{language.search}</p>
            <h2 id="saba-search-title">{language.search_saba_university}</h2>
          </div>
          <button type="button" className={styles.closeButton} onClick={onClose} aria-label={language.search_close}>
            <FaTimes />
          </button>
        </div>

        <label className={styles.searchBox}>
          <FaSearch aria-hidden="true" />
          <input
            ref={inputRef}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={language.search_placeholder}
            aria-label={language.search_placeholder}
            autoComplete="off"
          />
          {query ? (
            <button type="button" onClick={() => setQuery("")} aria-label={language.search_clear}>
              <FaTimes />
            </button>
          ) : null}
        </label>

        <SearchFilters activeType={activeType} onChange={setActiveType} compact />

        <div className={styles.resultPanel}>
          {statusContent}
          {results.length > 0 ? (
            <>
              <div className={styles.resultList} role="listbox" aria-label={language.search_results}>
                {results.map((result, index) => (
                  <SearchResultItem
                    key={`${result.type}-${result.id}`}
                    result={result}
                    query={trimmedQuery}
                    selected={index === selectedIndex}
                    onClick={() => openResult(result)}
                  />
                ))}
              </div>
              <button type="button" className={styles.viewAllButton} onClick={() => openFullSearch()}>
                {language.search_view_all_results}
              </button>
            </>
          ) : null}
          {!trimmedQuery && recentSearches.length > 0 ? (
            <div className={styles.recentSearches}>
              <p>{language.search_recent}</p>
              {recentSearches.map((item) => (
                <button type="button" key={item} onClick={() => setQuery(item)}>
                  {item}
                </button>
              ))}
            </div>
          ) : null}
        </div>
      </section>
    </div>
  );
}

export default SearchDialog;
